import { loadMembers } from '#~/api/agent/registry.js';
import { toHexFromId, toIdFromHex } from '#~/api/utils.js';
import { useAuth0 } from '#~/auth/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { UserInvite } from './user-invite.js';
import type { Api as DialogApi, Props as DialogProps } from '@studio/design-system/dialog';
import { Message, Prompt } from '@studio/design-system/prompt';
import { forwardRef, useEffect, useState } from 'react';
import type { ReactElement } from 'react';

/** @internal */
export type UserInviteDialogProps = DialogProps & {
  /** The organization ID. */
  organizationId: string;

  /** The room ID. */
  roomId: string;

  onClose: () => void;
};

/** @internal */
export const UserInviteDialog = forwardRef<DialogApi, UserInviteDialogProps>(
  (
    { organizationId: organizationIdHex, roomId: roomIdHex, onClose },
    forwardedRef
  ): ReactElement => {
    const roomId = toIdFromHex(roomIdHex);
    const organizationId = toIdFromHex(organizationIdHex);

    const [roomMemberRecords, roomInviteRecords, produce] = useDepot((µ) => [
      µ.roomMembershipRecords,
      µ.roomInviteRecords,
      µ.produce
    ]);

    const [refreshedMembers, setRefreshedMembers] = useState(false);

    const { getAccessTokenSilently } = useAuth0();

    useEffect(() => {
      const fn = async () => {
        if (refreshedMembers) {
          return;
        }

        setRefreshedMembers(true);

        const token = await getAccessTokenSilently();

        const { members, invites } = await loadMembers(
          toHexFromId(roomId)!,
          toHexFromId(organizationId)!,
          token
        );

        produce((draft) => {
          draft.roomMembershipRecords = members;
          draft.roomInviteRecords = invites;
        });
      };

      void fn();
    }, [refreshedMembers, roomId, organizationId, getAccessTokenSilently, produce]);

    const content = (
      <>
        {!!roomId && !!organizationId && (
          <UserInvite
            organizationId={organizationId}
            roomId={roomId}
            roomMemberRecords={roomMemberRecords}
            roomInviteRecords={roomInviteRecords}
            onClose={onClose}
          />
        )}
      </>
    );

    return (
      <div>
        <Prompt
          key={`invite_modal_${roomIdHex}`}
          className='w-[530px] p-0'
          dismissClassName='top-32'
          message={<Message>{content}</Message>}
          panelClassName='w-[530px] p-0'
          ref={forwardedRef}
        />
      </div>
    );
  }
);
